import { formatDateMonthDay } from "../../../utils/dateUtils";
import type { TreatmentOverview } from "../hooks/useTreatmentOverviews";
import {
  isLabTreatmentOverview,
  isOtcTreatmentOverview,
  isRxTreatmentOverview,
  isSupplementOtcTreatment,
  type LabTreatmentOverview,
  type OtcTreatmentOverview,
  type RxTreatmentOverview,
} from "../treatmentOverviewGuards";

/**
 * Dose banner mode for the treatment detail page (ME-5697 / ME-5699).
 *
 * - `starting` — no last log; `initialDose` present
 * - `lastSmartdose` / `lastDose` — `latestDoseTaken` present
 * - `none` — neither dose field available
 */
export type DoseBannerMode = "starting" | "lastSmartdose" | "lastDose" | "none";

/**
 * Copy variant for shared shipping status cards.
 * Supplements reuse the Rx card chrome with product-specific strings.
 */
export type TreatmentStatusCardCopyVariant =
  | "medication"
  | "supplement"
  | "lab";

/**
 * Discriminated status card model for treatment detail (ME-5698).
 * `null` means the treatment status does not map to a detail card.
 *
 * Variant-specific fields live only on the types that need them.
 * Refill shipping-soon / refill-ships reuse the same cards as first ship.
 */
export type TreatmentStatusCardModel =
  | {
      type: "shippingSoon";
      copyVariant?: TreatmentStatusCardCopyVariant;
    }
  | {
      type: "shipped";
      trackShipmentUrl: string | null;
      copyVariant?: TreatmentStatusCardCopyVariant;
      /**
       * Catalog English copy for in-transit storage handling (e.g. refrigerate).
       * Null when the catalog has no instruction; the card omits the body.
       */
      storageInstruction?: string | null;
    }
  | {
      type: "replacementShipped";
      trackShipmentUrl: string | null;
      copyVariant?: TreatmentStatusCardCopyVariant;
    }
  | {
      type: "delivered";
      copyVariant?: TreatmentStatusCardCopyVariant;
      /**
       * Catalog English copy for post-delivery handling (e.g. refrigerate).
       * Null when the catalog has no instruction; the card omits the body.
       */
      storageInstruction?: string | null;
    }
  | {
      type: "refills";
      nextRefillDate: string;
      copyVariant?: TreatmentStatusCardCopyVariant;
    }
  | {
      type: "refillAvailable";
      refillIntakeDeeplink: string;
      deadlineDate: string | null;
    }
  | { type: "refillInReview" }
  | {
      type: "labNextKitShipped";
      trackShipmentUrl: string | null;
    }
  | {
      type: "labDelivered";
      deliveredDate: string | null;
      subsequentKit: boolean;
    }
  | {
      type: "completedKitShipped";
      trackShipmentUrl: string | null;
      subsequentKit: boolean;
    }
  | { type: "completedKitReceived"; subsequentKit: boolean };

export type LabResultsCardModel =
  | { type: "resultsPending"; viewResultsUrl: string | null }
  | {
      type: "lastResultsCollected";
      resultsDate: string | null;
      viewResultsUrl: string | null;
    }
  | {
      type: "resultsReady";
      resultsDate: string | null;
      viewResultsUrl: string | null;
    };

export type ProductPresentation = "lockupBand" | "labResults";

export type TreatmentDetailViewModel = {
  doseBannerMode: DoseBannerMode;
  statusCard: TreatmentStatusCardModel | null;
  resultsCard: LabResultsCardModel | null;
  showMedLogCtAs: boolean;
  productPresentation: ProductPresentation;
  cardSpacing: 3 | 4;
};

type RefillsStatusCard = Extract<
  TreatmentStatusCardModel,
  { type: "refills" }
>;

/** Drop the date-only refills card when the date cannot be shown. */
const resolveRefillsStatusCard = (
  nextRefillDate: string | null | undefined,
  copyVariant?: TreatmentStatusCardCopyVariant
): RefillsStatusCard | null => {
  if (!nextRefillDate || !formatDateMonthDay(nextRefillDate)) {
    return null;
  }

  return copyVariant
    ? { type: "refills", nextRefillDate, copyVariant }
    : { type: "refills", nextRefillDate };
};

const resolveDoseBannerMode = (
  treatment: RxTreatmentOverview
): DoseBannerMode => {
  if (treatment.latestDoseTaken) {
    return treatment.latestDoseTaken.isSmartDose ? "lastSmartdose" : "lastDose";
  }

  if (
    treatment.isMedicationLogSupported === true &&
    treatment.initialDose?.dosage
  ) {
    return "starting";
  }

  return "none";
};

const resolveOtcStatusCard = (
  treatment: OtcTreatmentOverview
): TreatmentStatusCardModel | null => {
  // Shipping status cards with supplement copy are for supplements only.
  // Biomarker OTC can still open detail via deeplink/refresh; do not surface
  // "Your supplement…" there.
  if (!isSupplementOtcTreatment(treatment)) {
    return null;
  }

  const { status } = treatment;

  switch (status?.__typename) {
    case "TreatmentOverviewStatusShippingSoon":
      return { type: "shippingSoon", copyVariant: "supplement" };
    case "TreatmentOverviewStatusShipped":
      return {
        type: "shipped",
        trackShipmentUrl: status.trackingUrl ?? null,
        copyVariant: "supplement",
      };
    case "TreatmentOverviewStatusReplacementShipped":
      return {
        type: "replacementShipped",
        trackShipmentUrl: status.trackingUrl ?? null,
        copyVariant: "supplement",
      };
    case "TreatmentOverviewStatusDelivered":
      return { type: "delivered", copyVariant: "supplement" };
    case "TreatmentOverviewStatusRefills":
      return resolveRefillsStatusCard(status.nextRefillDate, "supplement");
    default:
      return null;
  }
};

const normalizeStorageInstruction = (
  value: string | null | undefined
): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const resolveStatusCard = (
  treatment: RxTreatmentOverview
): TreatmentStatusCardModel | null => {
  const { status, isRefill } = treatment;

  switch (status?.__typename) {
    case "TreatmentOverviewStatusShippingSoon":
      return { type: "shippingSoon" };
    case "TreatmentOverviewStatusShipped":
      // Refill ships reuses the same shipped card (track URL when present).
      return {
        type: "shipped",
        trackShipmentUrl: status.trackingUrl ?? null,
        storageInstruction: normalizeStorageInstruction(
          status.storageInstruction
        ),
      };
    case "TreatmentOverviewStatusDelivered":
      return {
        type: "delivered",
        storageInstruction: normalizeStorageInstruction(
          status.storageInstruction
        ),
      };
    case "TreatmentOverviewStatusRefills":
      // Care tab "Prescription refills on MM/DD" → Figma "Refills on mm/dd".
      return resolveRefillsStatusCard(status.nextRefillDate);
    case "TreatmentOverviewStatusIntakeIncomplete": {
      // Prescription detail only shows the refill check-in card. New intake
      // incomplete is handled on Care tab / homepage, not here.
      if (!isRefill) {
        return null;
      }
      const refillIntakeDeeplink = status.ctaDeeplink;
      if (!refillIntakeDeeplink) {
        return null;
      }
      return {
        type: "refillAvailable",
        refillIntakeDeeplink,
        deadlineDate: status.deadlineDate ?? null,
      };
    }
    case "TreatmentOverviewStatusInReview":
      // Same split as Care tab status rows: refill review → card; new intake
      // review → no prescription-page card.
      return isRefill ? { type: "refillInReview" } : null;
    default:
      return null;
  }
};

const normalizeUrl = (url: string | null | undefined): string | null => {
  const trimmed = url?.trim();
  return trimmed ? trimmed : null;
};

const resolveLabDetailCards = (
  treatment: LabTreatmentOverview
): Pick<TreatmentDetailViewModel, "statusCard" | "resultsCard"> => {
  const previousDetails = treatment.previousDetails ?? null;
  const previousResultsUrl = normalizeUrl(previousDetails?.resultsUrl);
  const lastCollected: LabResultsCardModel | null = previousDetails
    ? {
        type: "lastResultsCollected",
        resultsDate: previousDetails.resultsDate ?? null,
        viewResultsUrl: previousResultsUrl,
      }
    : null;

  const { status } = treatment;
  switch (status?.__typename) {
    case "TreatmentOverviewStatusShippingSoon":
      return {
        statusCard: { type: "shippingSoon", copyVariant: "lab" },
        resultsCard: lastCollected,
      };
    case "TreatmentOverviewStatusShipped":
    case "TreatmentOverviewStatusReplacementShipped": {
      const trackShipmentUrl = normalizeUrl(status.trackingUrl);
      return {
        statusCard: previousDetails
          ? { type: "labNextKitShipped", trackShipmentUrl }
          : { type: "shipped", trackShipmentUrl, copyVariant: "lab" },
        resultsCard: lastCollected,
      };
    }
    case "TreatmentOverviewStatusDelivered":
      return {
        statusCard: {
          type: "labDelivered",
          deliveredDate: status.deliveredDate ?? null,
          subsequentKit: !!previousDetails,
        },
        resultsCard: lastCollected,
      };
    case "TreatmentOverviewStatusCompletedKitShipped":
      return {
        statusCard: {
          type: "completedKitShipped",
          trackShipmentUrl: normalizeUrl(status.trackingUrl),
          subsequentKit: !!previousDetails,
        },
        resultsCard: {
          type: "resultsPending",
          viewResultsUrl: previousResultsUrl,
        },
      };
    case "TreatmentOverviewStatusCompletedKitReceived":
      return {
        statusCard: {
          type: "completedKitReceived",
          subsequentKit: !!previousDetails,
        },
        resultsCard: {
          type: "resultsPending",
          viewResultsUrl: previousResultsUrl,
        },
      };
    case "TreatmentOverviewStatusResultsAvailable":
      return {
        statusCard: null,
        resultsCard: {
          type: "resultsReady",
          resultsDate: status.resultsDate ?? null,
          viewResultsUrl: normalizeUrl(status.resultsUrl),
        },
      };
    default:
      return { statusCard: null, resultsCard: null };
  }
};

/**
 * Maps a `TreatmentOverview` onto independent treatment-detail dimensions.
 *
 * Rx status card, driven only by `status`:
 * | Status                              | Card                              |
 * | ----------------------------------- | --------------------------------- |
 * | ShippingSoon                        | shippingSoon                      |
 * | Shipped                             | shipped + tracking URL            |
 * | Delivered                           | delivered                         |
 * | Refills                             | refills + next refill date        |
 * | IntakeIncomplete + refill           | refillAvailable + deadline/intake |
 * | InReview + refill                   | refillInReview                    |
 *
 * Rx dose/logging UI, driven independently by dose fields:
 * | Dose data                           | UI                                |
 * | ----------------------------------- | --------------------------------- |
 * | latestDoseTaken                     | lastDose/lastSmartdose banner     |
 * | initialDose + log supported         | starting banner                   |
 * | log supported                       | med-log CTAs                      |
 *
 * These dimensions may appear together, such as a shipped status card with a
 * last-SmartDose banner and med-log CTAs.
 *
 * OTC supplements: shippingSoon / shipped (+ Track) /
 * replacementShipped (+ Track) / delivered / refills with next-shipment copy.
 * No dose banner or med log.
 *
 * Other non-Rx treatments return a no-op view model.
 */
export const getTreatmentDetailViewModel = (
  treatment: TreatmentOverview
): TreatmentDetailViewModel => {
  if (isOtcTreatmentOverview(treatment)) {
    return {
      doseBannerMode: "none",
      statusCard: resolveOtcStatusCard(treatment),
      resultsCard: null,
      showMedLogCtAs: false,
      productPresentation: "lockupBand",
      cardSpacing: 3,
    };
  }

  if (isLabTreatmentOverview(treatment)) {
    return {
      doseBannerMode: "none",
      ...resolveLabDetailCards(treatment),
      showMedLogCtAs: false,
      productPresentation: "labResults",
      cardSpacing: 4,
    };
  }

  if (!isRxTreatmentOverview(treatment)) {
    return {
      doseBannerMode: "none",
      statusCard: null,
      resultsCard: null,
      showMedLogCtAs: false,
      productPresentation: "lockupBand",
      cardSpacing: 3,
    };
  }

  return {
    doseBannerMode: resolveDoseBannerMode(treatment),
    statusCard: resolveStatusCard(treatment),
    resultsCard: null,
    showMedLogCtAs: treatment.isMedicationLogSupported === true,
    productPresentation: "lockupBand",
    cardSpacing: 3,
  };
};
